import { Caption1, Field, Input, Select, Text, makeStyles } from '@fluentui/react-components';
import { formatRecordedCalendarDate, scheduleChoices, servicePlacesWords, type ActivationDetails, type ActivationFill, type ActivationField, type ActivationOfferSource } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { PlacePicker } from '@/features/places';

const useStyles = makeStyles({
  group: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    paddingBlock: tokens.spacingVerticalS, paddingInline: tokens.spacingHorizontalM,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium,
  },
  pair: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', gap: tokens.spacingHorizontalM },
  quiet: { color: tokens.colorNeutralForeground3 },
});

export interface ActivationDetailsFieldsProps {
  /** What the fields show now: the record's values, the offer's where the record is empty, and what HR has changed. */
  readonly values: ActivationDetails;
  readonly fill: ActivationFill;
  readonly source: ActivationOfferSource;
  /** True while the person's offers are still being read. */
  readonly reading: boolean;
  /** The offers could not be read: nothing was filled, and HR can still type everything. */
  readonly unreadable: boolean;
  readonly disabled: boolean;
  readonly onChange: (change: Partial<ActivationDetails>) => void;
}

const classificationWords = { w2: 'W-2 employee', '1099': '1099 contractor' } as const;

/** What the record holds for one field, in words, for the hint beside a different value from the offer. */
function recordSays(field: ActivationField, recorded: ActivationDetails): string {
  switch (field) {
    case 'positionTitle': return recorded.positionTitle;
    case 'department': return recorded.department;
    case 'employmentClassification': return recorded.employmentClassification === '' ? '' : classificationWords[recorded.employmentClassification];
    case 'employmentType': return recorded.employmentType;
    case 'firstDay': return formatRecordedCalendarDate(recorded.firstDay);
    case 'servicePlaces': return servicePlacesWords(recorded.servicePlaces);
  }
}

/**
 * The employment details HR checks before a hire is made Active (#764): title, department, W-2 or 1099,
 * schedule, first day and service areas, filled from the signed offer (the offer's value wins; a different value on the record is shown beside it). The fields are the record's own: a
 * correction here is what is saved, through the ordinary person update, and only when HR presses Change stage.
 *
 * Four honest states, and the first line says which: filled from a signed offer; an offer signed on paper (the
 * app holds the letter and the date, not its terms, so nothing is filled); no offer in this candidacy (staff
 * added by hand, ADR-016: the fields are as the record holds them); or the offers could not be read.
 */
export function ActivationDetailsFields({ values, fill, source, reading, unreadable, disabled, onChange }: ActivationDetailsFieldsProps) {
  const styles = useStyles();
  const hint = (field: ActivationField): string | undefined => {
    if (!fill.differs.includes(field)) return undefined;
    return `The record says: ${recordSays(field, fill.recorded)}. The offer’s value is shown; change it if the record is right.`;
  };
  const note = reading ? 'Reading the signed offer…'
    : unreadable ? 'The signed offer could not be read, so nothing was filled in. You can type each value.'
      : source.kind === 'portal' ? 'Filled in from the signed offer. Check each value; nothing is saved until you press Change stage.'
        : source.kind === 'paper' ? `This offer was signed on paper on ${formatRecordedCalendarDate(source.signedAt)}. The app holds the signed letter but not its terms, so nothing was filled in.`
          : null;
  const filled = (field: ActivationField) => fill.filledFrom.includes(field);
  const schedule = scheduleChoices(values.employmentType);
  return (
    <section className={styles.group} aria-label="Employment details">
      <Text weight="semibold">Employment details</Text>
      {note === null ? null : <Caption1 role="status">{note}</Caption1>}
      <div className={styles.pair}>
        <Field label="Position title" hint={hint('positionTitle')}>
          <Input value={values.positionTitle} disabled={disabled} onChange={(_, d) => onChange({ positionTitle: d.value })} />
        </Field>
        <Field label="Department" hint={hint('department')}>
          <Input value={values.department} disabled={disabled} onChange={(_, d) => onChange({ department: d.value })} />
        </Field>
      </div>
      <div className={styles.pair}>
        <Field label="W-2 or 1099" hint={hint('employmentClassification')}>
          <Select value={values.employmentClassification} disabled={disabled} onChange={(_, d) => onChange({ employmentClassification: d.value === 'w2' || d.value === '1099' ? d.value : '' })}>
            <option value="">Not recorded</option>
            <option value="w2">{classificationWords.w2}</option>
            <option value="1099">{classificationWords['1099']}</option>
          </Select>
        </Field>
        <Field label="Schedule type" hint={hint('employmentType')}>
          <Select value={values.employmentType} disabled={disabled} onChange={(_, d) => onChange({ employmentType: d.value })}>
            <option value="">Not recorded</option>
            {schedule.map((type) => <option key={type} value={type}>{type}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="First day" required hint={hint('firstDay') ?? (filled('firstDay') ? 'From the offer’s proposed start date.' : undefined)}>
        <Input type="date" value={values.firstDay} disabled={disabled} onChange={(_, d) => onChange({ firstDay: d.value })} />
      </Field>
      <PlacePicker value={values.servicePlaces} onChange={(places) => onChange({ servicePlaces: places })} />
      {hint('servicePlaces') === undefined ? null : <Caption1 className={styles.quiet}>{hint('servicePlaces')}</Caption1>}
      {values.servicePlaces.length === 0 && fill.typedServiceArea !== null ? (
        <Caption1 className={styles.quiet}>The offer was written with a typed service area: “{fill.typedServiceArea}”. Pick the places above.</Caption1>
      ) : null}
    </section>
  );
}
